/**
 * API client using axios
 */

import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

// * Proactive refresh threshold (5 minutes before expiry)
const REFRESH_THRESHOLD_SECONDS = 5 * 60

// * Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // * 10 minutes (VDL sync with large projects can take time)
})

// * Decode JWT to get expiration time
const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split('.')[1]
    const decoded = JSON.parse(atob(payload))
    return decoded.exp || null
  } catch {
    return null
  }
}

// * Check if token needs refresh (expires within threshold)
const shouldRefreshToken = (token: string): boolean => {
  const exp = getTokenExpiry(token)
  if (!exp) return false

  const now = Math.floor(Date.now() / 1000)
  const timeUntilExpiry = exp - now

  return timeUntilExpiry > 0 && timeUntilExpiry < REFRESH_THRESHOLD_SECONDS
}

// * Track if token refresh is in progress to avoid multiple simultaneous refresh requests
let isRefreshing = false
let refreshPromise: Promise<void> | null = null

// * Proactive token refresh
const proactiveRefresh = async (): Promise<void> => {
  const { accessToken, refreshToken, updateTokens } = useAuthStore.getState()

  if (!accessToken || !refreshToken) return
  if (!shouldRefreshToken(accessToken)) return

  // * If already refreshing, wait for it
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      )

      const { access_token, refresh_token } = response.data
      updateTokens(access_token, refresh_token)
    } catch (error) {
      // * Proactive refresh failed - don't logout, let 401 handler deal with it
      console.warn('Proactive token refresh failed:', error)
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// * Request interceptor (add auth token + proactive refresh)
apiClient.interceptors.request.use(
  async (config) => {
    // * Skip refresh for auth endpoints
    if (!config.url?.includes('/auth/')) {
      await proactiveRefresh()
    }

    const { accessToken } = useAuthStore.getState()

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  (error) => Promise.reject(error)
)

// * Queue for requests waiting for token refresh (fallback for 401)
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  
  failedQueue = []
}

// * Response interceptor (handle errors and auto-refresh tokens)
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
    
    // * Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // * Don't retry refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        const { clearAuth } = useAuthStore.getState()
        clearAuth()
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        
        return Promise.reject(error)
      }
      
      // * If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            // * Retry original request with new token from store
            const { accessToken } = useAuthStore.getState()
            if (originalRequest.headers && accessToken) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
            return apiClient(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }
      
      originalRequest._retry = true
      isRefreshing = true
      
      const { refreshToken, updateTokens, clearAuth } = useAuthStore.getState()
      
      if (!refreshToken) {
        // * No refresh token available
        isRefreshing = false
        clearAuth()
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        
        return Promise.reject(error)
      }
      
      try {
        // * Try to refresh the token
        // * Use axios directly with skipAuthRefresh flag to avoid infinite loop
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000
          }
        )
        
        const { access_token, refresh_token } = response.data
        
        // * Update tokens in store
        updateTokens(access_token, refresh_token)
        
        // * Update authorization header for original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }
        
        // * Process queued requests
        processQueue(null)
        isRefreshing = false
        
        // * Retry original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        // * Refresh failed - logout user
        processQueue(refreshError as Error)
        isRefreshing = false
        
        clearAuth()
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
)

// * Workflow state types (mirror of backend WorkflowStateResponse)
export interface ActiveBatchInfo {
  id: number
  workflow_step: string | null
  status: string
  total_urls: number
  processed_urls: number
  successful: number
  failed: number
  started_at: string | null
  completed_at: string | null
  created_at: string
  config_version: number | null
}

export interface ActiveParsingTaskInfo {
  task_id: string
  status: string
  progress: number
  current_step: string | null
  domains_count: number
  urls_count: number
  uploaded_to_vdl: boolean
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface WorkflowStateResponse {
  project_id: number
  active_batch: ActiveBatchInfo | null
  active_parsing_task: ActiveParsingTaskInfo | null
  has_active_workflow: boolean
  current_config_version: number | null
}

// * API helper functions
export const api = {
  // * Auth
  login: async (login: string, password: string) => {
    const response = await apiClient.post('/auth/login', { login, password })
    return response.data
  },

  // * Embed (iframe): exchange a VDL user token for an AFI session
  embedExchange: async (vdlToken: string, crmId?: number) => {
    const response = await apiClient.post('/embed/exchange', {
      vdl_token: vdlToken,
      crm_id: crmId,
    })
    return response.data as {
      access_token: string
      token_type: string
      operator: { id: number; username: string; full_name?: string; role: string; is_active: boolean }
      expires_in: number
    }
  },

  refreshToken: async (refreshToken: string) => {
    const response = await apiClient.post('/auth/refresh', { refresh_token: refreshToken })
    return response.data
  },
  
  logout: async () => {
    const response = await apiClient.post('/auth/logout')
    return response.data
  },
  
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me')
    return response.data
  },
  
  // * Projects
  getProjects: async (params?: {
    page?: number
    page_size?: number
    search?: string
    status?: string
    parse_manual?: boolean
    vdl_manager_id?: number
  }) => {
    const response = await apiClient.get('/projects', { params })
    return response.data
  },

  getVdlManagers: async (): Promise<Array<{ id: number; fio: string }>> => {
    const response = await apiClient.get('/projects/vdl-managers')
    return response.data
  },

  syncVdlManagers: async () => {
    const response = await apiClient.post('/projects/sync-managers')
    return response.data
  },
  
  getProject: async (projectId: number) => {
    const response = await apiClient.get(`/projects/${projectId}`)
    return response.data
  },

  /**
   * Aggregated project-level workflow state for UnifiedParsingModal recovery.
   * Replaces 3 separate Zustand store lookups + 1 localStorage read.
   * (Distinct from getWorkflowState below, which is keyed by parsing task_id.)
   */
  getProjectWorkflowState: async (projectId: number): Promise<WorkflowStateResponse> => {
    const response = await apiClient.get(`/projects/${projectId}/workflow-state`)
    return response.data
  },

  getVdlTz: async (projectId: number) => {
    const response = await apiClient.get(`/projects/${projectId}/vdl-tz`)
    return response.data
  },
  
  getProjectScript: async (projectId: number) => {
    const response = await apiClient.get(`/projects/${projectId}/script`)
    return response.data
  },
  
  syncProjectSources: async (projectId: number) => {
    const response = await apiClient.post(`/projects/${projectId}/sync`)
    return response.data
  },
  
  // * Sources
  getSources: async (params: {
    project_id: number
    page?: number
    page_size?: number
    status?: string
    source_type?: string
    ai_score_min?: number
    ai_score_max?: number
    source_from?: string
    search?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const response = await apiClient.get('/sources', { params })
    return response.data
  },
  
  getSource: async (sourceId: number) => {
    const response = await apiClient.get(`/sources/${sourceId}`)
    return response.data
  },
  
  updateSource: async (sourceId: number, data: any) => {
    const response = await apiClient.patch(`/sources/${sourceId}`, data)
    return response.data
  },
  
  getNextBatch: async (projectId: number, count: number = 10) => {
    const response = await apiClient.post('/sources/next-batch', null, {
      params: { project_id: projectId, count },
    })
    return response.data
  },
  
  releaseSource: async (sourceId: number) => {
    const response = await apiClient.post(`/sources/${sourceId}/release`)
    return response.data
  },
  
  getMyLockedSources: async (projectId?: number) => {
    const response = await apiClient.get('/sources/locked/my', {
      params: projectId ? { project_id: projectId } : undefined,
    })
    return response.data
  },
  
  releaseAllSources: async (projectId?: number) => {
    const response = await apiClient.post('/sources/release-all', null, {
      params: projectId ? { project_id: projectId } : undefined,
    })
    return response.data
  },
  
  // * Project statistics and config
  getProjectStatistics: async (projectId: number) => {
    const response = await apiClient.get(`/projects/${projectId}/statistics`)
    return response.data
  },
  
  generateProjectConfig: async (projectId: number) => {
    const response = await apiClient.post(`/projects/${projectId}/generate-config`)
    return response.data
  },

  generateInitialConfig: async (projectId: number) => {
    const response = await apiClient.post(`/projects/${projectId}/generate-initial-config`)
    return response.data
  },
  
  startCollection: async (projectId: number) => {
    const response = await apiClient.post(`/projects/${projectId}/start-collection`)
    return response.data
  },
  
  getProjectQueriesRegions: async (projectId: number) => {
    const response = await apiClient.get(`/projects/${projectId}/queries-regions`)
    return response.data
  },
  
  getProjectInfoFromCrm: async (crmId: number) => {
    const response = await apiClient.get(`/projects/info/${crmId}`)
    return response.data
  },
  
  // * Parser
  startParsing: async (data: { 
    crm_id: number
    force_collect?: boolean
    credentials_id?: number
    main_query?: string
    region_code?: string
    excluded_words?: string[]
    min_clicks_threshold?: number
    download_all_queries?: boolean
    top_queries_count?: number
  }) => {
    const response = await apiClient.post('/parser/start', data)
    return response.data
  },
  
  startBatchParsing: async (data: { 
    crm_id: number
    force_collect?: boolean
    credentials_id?: number
    queries: string[]
    region_codes: string[]
    excluded_words?: string[]
    min_clicks_threshold?: number
    download_all_queries?: boolean
    top_queries_count?: number
  }) => {
    const response = await apiClient.post('/parser/start-batch', data)
    return response.data
  },
  
  getParsingTasks: async () => {
    const response = await apiClient.get('/parser/tasks')
    return response.data
  },
  
  getParsingTaskById: async (taskId: string) => {
    const response = await apiClient.get(`/parser/task/${taskId}`)
    return response.data
  },
  
  getParsingStatus: async (taskId: string) => {
    const response = await apiClient.get(`/parser/status/${taskId}`)
    return response.data
  },

  cancelParsingTask: async (taskId: string) => {
    const response = await apiClient.post(`/parser/cancel-task/${taskId}`)
    return response.data
  },
  
  getParsingResult: async (taskId: string) => {
    const response = await apiClient.get(`/parser/result/${taskId}`)
    return response.data
  },
  
  processParsingResults: async (taskId: string, crmId: number) => {
    const response = await apiClient.post('/parser/process-results', {
      task_id: taskId,
      crm_id: crmId,
    })
    return response.data
  },
  
  getUploadStatus: async (uploadTaskId: string) => {
    const response = await apiClient.get(`/parser/upload-status/${uploadTaskId}`)
    return response.data
  },
  
  // * VDL Upload History
  getVdlUploadHistory: async (params?: {
    page?: number
    page_size?: number
    project_id?: number
    parsing_task_id?: string
    upload_task_id?: number
    upload_status?: string
    source_type?: string
  }) => {
    const response = await apiClient.get('/vdl-upload-history/', { params })
    return response.data
  },
  
  getVdlUploadTaskWithHistory: async (uploadTaskId: string) => {
    const response = await apiClient.get(`/vdl-upload-history/by-task/${uploadTaskId}`)
    return response.data
  },
  
  getVdlUploadStats: async (params?: {
    project_id?: number
    parsing_task_id?: string
  }) => {
    const response = await apiClient.get('/vdl-upload-history/stats', { params })
    return response.data
  },
  
  getProjectVdlUploadHistory: async (projectId: number, params?: {
    page?: number
    page_size?: number
    upload_status?: string
  }) => {
    const response = await apiClient.get(`/vdl-upload-history/by-project/${projectId}`, { params })
    return response.data
  },
  
  getRecentVdlUploads: async (limit: number = 20) => {
    const response = await apiClient.get('/vdl-upload-history/recent', {
      params: { limit },
    })
    return response.data
  },
  
  // * Parser Workflow (New step-by-step post-parsing)
  getProjectAIConfig: async (projectId: number) => {
    const response = await apiClient.get(`/parser/workflow/ai-config/${projectId}`)
    return response.data
  },
  
  analyzeDomain: async (params: { url: string; ai_config_id: number }) => {
    const response = await apiClient.post('/parser/workflow/analyze-domain', params)
    return response.data
  },
  
  analyzeDomainsBatch: async (params: { domains: string[]; ai_config_id: number }) => {
    const response = await apiClient.post('/parser/workflow/analyze-batch', params)
    return response.data
  },
  
  // * Async batch analysis (RQ background task)
  startBatchAnalysis: async (params: { domains: string[]; urls?: string[]; ai_config_id: number }) => {
    const response = await apiClient.post('/parser/workflow/analyze-batch-async', params)
    return response.data
  },
  
  getBatchStatus: async (batchId: number, options?: { includeResults?: boolean; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams()
    if (options?.includeResults === false) {
      params.append('include_results', 'false')
    }
    if (options?.page) {
      params.append('page', options.page.toString())
    }
    if (options?.pageSize) {
      params.append('page_size', options.pageSize.toString())
    }
    const queryString = params.toString()
    const url = `/parser/workflow/batch-status/${batchId}${queryString ? `?${queryString}` : ''}`
    const response = await apiClient.get(url)
    return response.data
  },
  
  cancelBatchAnalysis: async (batchId: number) => {
    const response = await apiClient.post(`/parser/workflow/batch-cancel/${batchId}`)
    return response.data
  },
  
  getProjectLatestBatch: async (projectId: number, options?: { page?: number; pageSize?: number }) => {
    const params = new URLSearchParams()
    if (options?.page) {
      params.append('page', options.page.toString())
    }
    if (options?.pageSize) {
      params.append('page_size', options.pageSize.toString())
    }
    const queryString = params.toString()
    const url = `/parser/workflow/project/${projectId}/latest-batch${queryString ? `?${queryString}` : ''}`
    const response = await apiClient.get(url)
    return response.data
  },

  // * Update batch workflow step (for state restoration after page refresh)
  updateBatchWorkflowStep: async (batchId: number, workflowStep: string) => {
    const response = await apiClient.patch(`/parser/workflow/batch/${batchId}/workflow-step`, {
      workflow_step: workflowStep
    })
    return response.data
  },

  bulkUpdateParsedUrlStatus: async (parsedUrlIds: number[], status: string) => {
    const response = await apiClient.patch('/parser/workflow/parsed-urls/bulk-status', {
      parsed_url_ids: parsedUrlIds,
      status
    })
    return response.data
  },

  getParsedUrls: async (params: {
    project_id: number
    parsing_task_id?: string
    source_status?: string
    has_path?: boolean
    page?: number
    page_size?: number
  }) => {
    const response = await apiClient.get('/parser/workflow/parsed-urls', { params })
    return response.data
  },

  // * Save approved URLs to parsed_urls table (called during Upload step)
  saveApprovedUrls: async (params: {
    project_id: number
    urls: Array<{
      url: string
      domain?: string
      path?: string
      ai_recommendation?: string
      ai_score?: number
      ai_reason?: string
      source_type?: string
    }>
  }) => {
    const response = await apiClient.post('/parser/workflow/save-approved-urls', params)
    return response.data
  },

  uploadDomainsToVDL: async (params: {
    crm_id: number
    domains: string[]
    subsource?: string
    task_id?: string
  }) => {
    const { task_id, ...bodyParams } = params
    const queryParams = task_id ? `?task_id=${encodeURIComponent(task_id)}` : ''
    const response = await apiClient.post(`/parser/workflow/upload-domains${queryParams}`, bodyParams)
    return response.data
  },
  
  // * Site proxying and iframe checking
  proxySite: async (url: string, keywords?: string) => {
    const params: any = { url }
    if (keywords) {
      params.keywords = keywords
    }
    const response = await apiClient.get('/sources/proxy-site', { params })
    return response.data
  },
  
  checkIframe: async (url: string) => {
    const response = await apiClient.get('/sources/check-iframe', {
      params: { url },
    })
    return response.data
  },
  
  // * History
  getMyHistory: async (params?: {
    page?: number
    page_size?: number
    project_id?: number
    status?: string
    rejected_reason?: string
  }) => {
    const response = await apiClient.get('/history/my', { params })
    return response.data
  },
  
  getAllHistory: async (params?: {
    page?: number
    page_size?: number
    project_id?: number
    operator_id?: number
    status?: string
    rejected_reason?: string
  }) => {
    const response = await apiClient.get('/history/all', { params })
    return response.data
  },
  
  exportHistory: async (params?: {
    project_id?: number
    operator_id?: number
    status?: string
    rejected_reason?: string
    tz_offset?: number
  }) => {
    const response = await apiClient.get('/history/export', {
      params,
      responseType: 'blob',
    })
    return response.data as Blob
  },

  getRejectedReasons: async (params?: {
    project_id?: number
  }) => {
    const response = await apiClient.get('/history/rejected-reasons', { params })
    return response.data
  },
  
  getMyStats: async (projectId?: number) => {
    const response = await apiClient.get('/history/stats/my', {
      params: projectId ? { project_id: projectId } : undefined,
    })
    return response.data
  },
  
  getAllStats: async (projectId?: number) => {
    const response = await apiClient.get('/history/stats/all', {
      params: projectId ? { project_id: projectId } : undefined,
    })
    return response.data
  },

  // * Earnings API
  getMyEarnings: async (params?: {
    period_start?: string
    period_end?: string
    preset?: string
  }) => {
    const response = await apiClient.get('/earnings/my', { params })
    return response.data
  },

  getAllEarnings: async (params?: {
    period_start?: string
    period_end?: string
    preset?: string
  }) => {
    const response = await apiClient.get('/earnings/all', { params })
    return response.data
  },

  getEarningsHistory: async (params?: {
    period_start?: string
    period_end?: string
  }) => {
    const response = await apiClient.get('/earnings/history', { params })
    return response.data
  },

  // * Workflow State API (backend storage)
  saveWorkflowState: async (taskId: string, state: any) => {
    const response = await apiClient.post('/parser/workflow/workflow-state/save', {
      task_id: taskId,
      state
    })
    return response.data
  },

  getWorkflowState: async (taskId: string) => {
    const response = await apiClient.get(`/parser/workflow/workflow-state/${taskId}`)
    return response.data
  },

  clearWorkflowState: async (taskId: string) => {
    const response = await apiClient.delete(`/parser/workflow/workflow-state/${taskId}`)
    return response.data
  },
  
  // * Admin endpoints
  adminUpdateSourceStatus: async (sourceId: number, data: {
    status: string
    will_work?: boolean
    rejected_reason?: string
    comment?: string
  }) => {
    const response = await apiClient.post(`/sources/${sourceId}/admin-status`, data)
    return response.data
  },
  
  getSourceStatusHistory: async (sourceId: number) => {
    const response = await apiClient.get(`/sources/${sourceId}/history`, {
      params: { page: 1, page_size: 100 },
    })
    return response.data
  },
  
  getAdminBlockedSources: async (projectId: number) => {
    const response = await apiClient.get('/sources/admin-blocked', {
      params: { project_id: projectId },
    })
    return response.data
  },

  // * Phone Extraction API

  // * Extract phones from single URL (synchronous)
  extractPhonesFromUrl: async (data: {
    parsed_url_id: number
    method: 'quick' | 'analyzer' | 'phone_parser' | 'manual'
    ai_config_id?: number
  }) => {
    const response = await apiClient.post('/phone-extraction/extract-from-url', data)
    return response.data
  },

  // * Start batch phone extraction (RQ background task)
  startBatchPhoneExtraction: async (data: {
    project_id: number
    parsing_task_id?: string
    parsed_url_ids: number[]
    methods: string[]
  }) => {
    const response = await apiClient.post('/phone-extraction/extract-batch-async', data)
    return response.data
  },

  // * Get phone extraction task status
  getPhoneExtractionTaskStatus: async (taskId: number) => {
    const response = await apiClient.get(`/phone-extraction/task-status/${taskId}`)
    return response.data
  },

  // * Get phone extraction tasks list
  getPhoneExtractionTasks: async (params: {
    project_id: number
    page?: number
    page_size?: number
  }) => {
    const response = await apiClient.get('/phone-extraction/tasks', { params })
    return response.data
  },

  // * Add manual phone to URL
  addManualPhone: async (data: {
    parsed_url_id: number
    phone_number: string
    phone_type?: 'mobile' | 'landline' | 'unknown'
  }) => {
    const response = await apiClient.post('/phone-extraction/add-manual-phone', data)
    return response.data
  },

  // * Remove extracted phone
  removePhone: async (phoneId: number) => {
    const response = await apiClient.delete(`/phone-extraction/remove-phone/${phoneId}`)
    return response.data
  },

  // * Get phones for specific URL
  getUrlPhones: async (parsedUrlId: number) => {
    const response = await apiClient.get(`/phone-extraction/url/${parsedUrlId}/phones`)
    return response.data
  },

  // * Extract phones from source (domain)
  extractPhonesFromSource: async (data: {
    source_id: number
    methods?: string[]
  }) => {
    const response = await apiClient.post('/phone-extraction/extract-from-source', data)
    return response.data
  },

  // * Extract phones from multiple sources (domains) batch
  extractPhonesFromSourcesBatch: async (data: {
    project_id: number
    source_ids: number[]
    methods?: string[]
  }) => {
    const response = await apiClient.post('/phone-extraction/extract-from-sources-batch', data)
    return response.data
  },

  // * Toggle phone upload selection
  togglePhoneUpload: async (phoneId: number, selected: boolean) => {
    const response = await apiClient.patch(`/phone-extraction/phone/${phoneId}/toggle-upload`, {
      selected
    })
    return response.data
  },

  // * Upload phones to VDL
  uploadPhonesToVDL: async (params: {
    project_id: number
    crm_id: number
    phone_ids: number[]
    subsource?: string
  }) => {
    const response = await apiClient.post('/parser/workflow/upload-phones', params)
    return response.data
  },

  // * Upload manual phones to VDL (without DB IDs)
  uploadManualPhonesToVDL: async (params: {
    project_id: number
    crm_id: number
    phone_numbers: string[]
    subsource?: string
  }) => {
    const response = await apiClient.post('/parser/workflow/upload-manual-phones', params)
    return response.data
  },

  // * Save manual URLs to database
  saveManualUrls: async (data: {
    project_id: number
    parsing_task_id: string
    urls: string[]
  }) => {
    const response = await apiClient.post('/parser/workflow/save-manual-urls', data)
    return response.data
  },

  // ============================================================================
  // Swipe Mode API for Pages (ParsedURLs)
  // ============================================================================

  // * Get pages statistics for swipe mode
  getPagesStatistics: async (projectId: number) => {
    const response = await apiClient.get('/parser/workflow/pages/statistics', {
      params: { project_id: projectId },
    })
    return response.data
  },

  // * Get next batch of pages for review
  getNextPagesBatch: async (projectId: number, count: number = 10) => {
    const response = await apiClient.post('/parser/workflow/pages/next-batch', null, {
      params: { project_id: projectId, count },
    })
    return response.data
  },

  // * Get my locked pages
  getMyLockedPages: async (projectId?: number) => {
    const response = await apiClient.get('/parser/workflow/pages/locked/my', {
      params: projectId ? { project_id: projectId } : undefined,
    })
    return response.data
  },

  // * Release a page
  releasePage: async (pageId: number) => {
    const response = await apiClient.post(`/parser/workflow/pages/${pageId}/release`)
    return response.data
  },

  // * Release all pages
  releaseAllPages: async (projectId?: number) => {
    const response = await apiClient.post('/parser/workflow/pages/release-all', null, {
      params: projectId ? { project_id: projectId } : undefined,
    })
    return response.data
  },

  // * Update page review status
  updatePageReviewStatus: async (pageId: number, data: {
    review_status: 'approved' | 'rejected' | 'uncertain'
    rejected_reason?: string
  }) => {
    const response = await apiClient.patch(`/parser/workflow/pages/${pageId}/review`, data)
    return response.data
  },

  // * Auto Load History
  getAutoLoadHistory: async (params: {
    page?: number
    per_page?: number
    crm_id?: number
    status?: string
  }) => {
    const response = await apiClient.get('/auto-load-history', { params })
    return response.data
  },

  getAutoLoadTaskDetails: async (taskId: string) => {
    const response = await apiClient.get(`/auto-load-history/${taskId}`)
    return response.data
  },

  // * Reanalyze all sources (reset + re-queue with current AI config)
  reanalyzeProject: async (projectId: number) => {
    const response = await apiClient.post(`/projects/${projectId}/reanalyze`)
    return response.data
  },

  // * Force Re-analyze
  forceReanalyzeProject: async (projectId: number, sourceIds?: number[]) => {
    const response = await apiClient.post(`/analysis/project/${projectId}/force-reanalyze`, {
      source_ids: sourceIds || null,
    })
    return response.data
  },

  forceReanalyzeSource: async (sourceId: number) => {
    const response = await apiClient.post(`/analysis/source/${sourceId}/force-reanalyze`)
    return response.data
  },

  reanalyzeBatch: async (batchId: number) => {
    const response = await apiClient.post(`/analysis/batch/${batchId}/reanalyze`)
    return response.data as {
      batch_id: number
      mode: 'urls' | 'sources' | 'parsed_urls'
      total: number
      jobs?: string[]
      batch_ids?: number[]
      job?: string
    }
  },

  getAnalysisProgress: async (batchId: number) => {
    const response = await apiClient.get(`/analysis/batch/${batchId}/progress`)
    return response.data
  },
}

export default api

